import { useState } from 'react'
import type { Company } from '../../types'
import { useDataStore } from '../../stores/dataStore'
import { Button, Input, Textarea, ConfirmModal } from '../ui'
import { toast } from '../ui/Toast'
import ActivityFeed from '../timeline/ActivityFeed'
import TaskList from '../timeline/TaskList'
import { CompanyEmailHistory } from '../gmail'
import CompanyIntelligence from './CompanyIntelligence'

interface CompanyPanelProps {
  company: Company
  onClose: () => void
}

function extractDomain(input: string): string {
  if (!input) return ''
  return input
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '')
    .trim()
}

export default function CompanyPanel({ company, onClose }: CompanyPanelProps) {
  const { deals, contacts, updateCompany, deleteCompany } = useDataStore()
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)

  const [name, setName] = useState(company.name)
  const [website, setWebsite] = useState(company.website || '')
  const [industry, setIndustry] = useState(company.industry || '')
  const [notes, setNotes] = useState(company.notes || '')

  const companyDeals = deals.filter((d) => d.companyId === company.id)
  const companyContacts = contacts.filter((c) => c.companyId === company.id)

  const handleSave = async () => {
    if (!name.trim()) {
      toast('Name is required', 'error')
      return
    }

    try {
      await updateCompany(company.id, {
        name: name.trim(),
        domain: extractDomain(website) || undefined,
        website: website.trim() || undefined,
        industry: industry.trim() || undefined,
        notes: notes.trim() || undefined,
      })
      setIsEditing(false)
      toast('Company updated', 'success')
    } catch {
      toast('Failed to update company', 'error')
    }
  }

  const handleDelete = async () => {
    try {
      await deleteCompany(company.id)
      toast('Company deleted', 'info')
      onClose()
    } catch {
      toast('Failed to delete company', 'error')
    }
  }

  return (
    <>
      <aside className="w-80 bg-bg-surface border border-border rounded-lg flex flex-col shadow-card">
        <header className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="font-semibold text-text-primary truncate">
            {isEditing ? 'Edit company' : company.name}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-[#F5F5F3] rounded-[6px] transition-colors duration-150"
            aria-label="Close panel"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </header>

        <div className="flex-1 p-4 overflow-y-auto">
          {isEditing ? (
            <div className="space-y-4">
              <Input
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Input
                label="Website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://example.com"
              />
              <Input
                label="Industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
              />
              <Textarea
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          ) : (
            <div className="space-y-4">
              {company.website && (
                <div>
                  <p className="text-xs text-text-secondary mb-1">Website</p>
                  <a
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-action-primary hover:underline"
                  >
                    {company.website.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}

              {company.industry && (
                <div>
                  <p className="text-xs text-text-secondary mb-1">Industry</p>
                  <p className="text-sm">{company.industry}</p>
                </div>
              )}

              {company.notes && (
                <div>
                  <p className="text-xs text-text-secondary mb-1">Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{company.notes}</p>
                </div>
              )}

              {/* Company Intelligence Section */}
              <CompanyIntelligence company={company} />

              <div className="pt-4 border-t border-border">
                <p className="text-xs text-text-secondary mb-2">
                  Related ({companyDeals.length} deals, {companyContacts.length} contacts)
                </p>
                
                {companyDeals.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-text-primary mb-1">Deals</p>
                    <ul className="text-xs text-text-secondary space-y-0.5">
                      {companyDeals.slice(0, 3).map((d) => (
                        <li key={d.id}>• {d.title}</li>
                      ))}
                      {companyDeals.length > 3 && (
                        <li>... and {companyDeals.length - 3} more</li>
                      )}
                    </ul>
                  </div>
                )}

                {companyContacts.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-text-primary mb-1">Contacts</p>
                    <ul className="text-xs text-text-secondary space-y-0.5">
                      {companyContacts.slice(0, 3).map((c) => (
                        <li key={c.id}>• {c.name}</li>
                      ))}
                      {companyContacts.length > 3 && (
                        <li>... and {companyContacts.length - 3} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-border">
                <TaskList entityType="company" entityId={company.id} />
              </div>

              <div className="pt-4 border-t border-border">
                <ActivityFeed entityType="company" entityId={company.id} />
              </div>

              {companyContacts.length > 0 && (
                <div className="pt-4 border-t border-border">
                  <CompanyEmailHistory contacts={companyContacts} />
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="px-4 py-3 border-t border-border flex justify-between">
          {isEditing ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={handleSave}>
                Save
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setIsDeleteConfirmOpen(true)}
              >
                Delete
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            </>
          )}
        </footer>
      </aside>

      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete company"
        message="This will permanently delete this company. Related deals and contacts will not be deleted, but will lose their company association."
        confirmText="Delete"
        variant="danger"
      />
    </>
  )
}
